/**
 * Activity — SPEC §9.3.
 *
 * "Answers *did that reminder actually send?* and *who entered this on her behalf?*"
 *
 * Both are questions asked after the fact, usually because something looks wrong. The screen
 * is shaped around that: newest first, failures visible without hunting for them, and one
 * click to narrow to a single person.
 *
 * ## Failures are not a filter you have to think of
 *
 * The count of things that did not work sits at the top whether or not it is zero, and it is
 * a button. A skipped or failed send is silent everywhere else in the product — the
 * instructor simply never hears from us — so this is the only place it surfaces, and it must
 * not be reachable only by somebody who already suspected it.
 *
 * ## The sentences come from the server
 *
 * Nothing here interprets a payload. Each entry arrives with `summary`, `detail` and its raw
 * `key`, and this file lays them out. A renderer here would be a second copy of the server's
 * knowledge of what each action means, and it would go stale as a blank row rather than as
 * an error.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatCycle } from '@gearup/shared';
import {
  activityApi,
  type ActivityEntry,
  type ActivityQuery,
  type ActivitySource,
} from '../../lib/api.js';

const PAGE = 50;

export function Activity({
  cycleKey,
  instructorId,
  instructorName,
  onClearInstructor,
}: {
  /** For the availability export, which is per month. */
  cycleKey: string | null;
  /** Set when arriving from a roster row — "show me everything about her". */
  instructorId?: string;
  instructorName?: string;
  onClearInstructor?: () => void;
}) {
  const [entries, setEntries] = useState<ActivityEntry[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [source, setSource] = useState<ActivitySource | ''>('');
  const [actor, setActor] = useState('');
  const [failedOnly, setFailedOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [actors, setActors] = useState<string[]>([]);

  const query = useMemo<ActivityQuery>(
    () => ({
      ...(instructorId ? { instructorId } : {}),
      ...(source ? { source } : {}),
      ...(actor ? { actor } : {}),
      ...(failedOnly ? { failedOnly: true } : {}),
      ...(applied ? { search: applied } : {}),
      limit: PAGE,
    }),
    [instructorId, source, actor, failedOnly, applied],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const page = await activityApi.list(query);
      setEntries(page.entries);
      setCursor(page.nextCursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the activity log.');
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void activityApi
      .actors()
      .then(({ actors: list }) => setActors(list))
      .catch(() => undefined);
  }, []);

  const loadMore = async () => {
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const page = await activityApi.list({ ...query, cursor });
      // Appended, never replaced. Re-fetching from the top would re-show what has arrived
      // since, and an audit trail that reorders under the reader is one nobody trusts.
      setEntries((current) => [...current, ...page.entries]);
      setCursor(page.nextCursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load more.');
    } finally {
      setLoadingMore(false);
    }
  };

  const failures = entries.filter((e) => e.status === 'FAILED' || e.status === 'SKIPPED').length;

  return (
    <div className="stack">
      <div className="card stack">
        <div className="row row--between row--wrap">
          <div>
            <h2 style={{ margin: 0, fontSize: 'var(--text-lg)' }}>Activity</h2>
            <p className="small muted" style={{ margin: '4px 0 0' }}>
              Everything an admin did and every message the system sent, newest first.
            </p>
          </div>
          <div className="row" style={{ gap: 'var(--space-2)' }}>
            {/* Plain links. The browser downloads them itself, with the filename the server
                sets, and the session cookie goes along because it is same-origin. */}
            <a className="btn btn--secondary" href={activityApi.activityCsvUrl(query)}>
              Export this list
            </a>
            {/* Named, not "the month". The month picker is deliberately hidden on this tab —
                an activity log is not a month — so a button reading "export the month" is
                asking somebody to remember which month they last chose on a different
                screen. It exports whatever is selected there, and it has to say so. */}
            {cycleKey && (
              <a className="btn btn--secondary" href={activityApi.availabilityCsvUrl(cycleKey)}>
                Export {formatCycle(cycleKey)}
              </a>
            )}
          </div>
        </div>

        {instructorId && (
          <div className="notice notice--info" role="status">
            Showing only <strong>{instructorName ?? 'one instructor'}</strong>.{' '}
            {onClearInstructor && (
              <button
                type="button"
                className="btn btn--ghost"
                style={{ padding: 0, minHeight: 'auto' }}
                onClick={onClearInstructor}
              >
                Show everyone
              </button>
            )}
          </div>
        )}

        <div className="row row--wrap" style={{ gap: 'var(--space-2)', alignItems: 'flex-end' }}>
          <label className="field" style={{ flex: '1 1 220px' }}>
            <span className="field__label">Search</span>
            <input
              className="input"
              type="search"
              value={search}
              placeholder="Name, address or action"
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') setApplied(search.trim());
              }}
            />
          </label>
          <button className="btn btn--secondary" onClick={() => setApplied(search.trim())}>
            Search
          </button>

          <label className="field" style={{ minWidth: 150 }}>
            <span className="field__label">Kind</span>
            <select
              className="select"
              value={source}
              onChange={(e) => setSource(e.target.value as ActivitySource | '')}
            >
              <option value="">Everything</option>
              <option value="ADMIN">Admin actions</option>
              <option value="NOTIFICATION">Messages sent</option>
            </select>
          </label>

          <label className="field" style={{ minWidth: 180 }}>
            <span className="field__label">Done by</span>
            <select className="select" value={actor} onChange={(e) => setActor(e.target.value)}>
              <option value="">Anyone</option>
              {actors.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </label>
        </div>

        {/* Always shown, including at zero. "0 did not get through" is the reassurance;
            hiding the row when it is empty means its absence has to be interpreted. */}
        <div className="row row--between row--wrap" style={{ alignItems: 'center' }}>
          <button
            type="button"
            className={failedOnly ? 'btn btn--primary' : 'btn btn--secondary'}
            onClick={() => setFailedOnly((v) => !v)}
            aria-pressed={failedOnly}
          >
            {failedOnly ? 'Showing only what failed' : `Show only what failed`}
          </button>
          <span className="small muted">
            {failures === 0
              ? 'Nothing on this page failed to send.'
              : `${failures} on this page did not get through.`}
          </span>
        </div>
      </div>

      {error && (
        <div className="notice notice--error" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <p className="muted center">Loading…</p>
      ) : entries.length === 0 ? (
        <div className="card">
          <p className="muted center" style={{ margin: 0 }}>
            Nothing matches. {failedOnly && 'Try turning off "only what failed".'}
          </p>
        </div>
      ) : (
        <div className="card stack" style={{ gap: 0 }}>
          {entries.map((entry, index) => (
            <Row key={entry.id} entry={entry} first={index === 0} />
          ))}
        </div>
      )}

      {cursor && (
        <button className="btn btn--secondary btn--block" disabled={loadingMore} onClick={loadMore}>
          {loadingMore ? 'Loading…' : 'Load older'}
        </button>
      )}
    </div>
  );
}

function Row({ entry, first }: { entry: ActivityEntry; first: boolean }) {
  const failed = entry.status === 'FAILED';
  const skipped = entry.status === 'SKIPPED';

  return (
    <div
      className="stack"
      style={{
        gap: '2px',
        padding: 'var(--space-2) 0',
        borderTop: first ? 'none' : '1px solid var(--border)',
      }}
    >
      <div className="row row--between row--wrap" style={{ gap: 'var(--space-2)' }}>
        <span style={{ fontWeight: failed ? 700 : 500 }}>
          {/* The one place color alone is not enough: a failed send and a sent one read the
              same in grayscale otherwise (§12.2). */}
          {failed && <span aria-hidden="true">! </span>}
          {entry.summary}
        </span>
        <span className="small muted" style={{ whiteSpace: 'nowrap' }}>
          <time dateTime={entry.at}>{when(entry.at)}</time>
        </span>
      </div>

      {entry.detail && (
        <span className="small" style={{ color: failed || skipped ? 'var(--brand-red)' : 'var(--text-muted)' }}>
          {entry.detail}
        </span>
      )}

      <span className="small muted">
        {entry.actor ? `by ${entry.actor}` : entry.channel === 'SLACK' ? 'Slack' : 'automatic'}
        {' · '}
        {/* The raw key, always. When the server has no sentence for an action this is what
            says which action it was. */}
        <code>{entry.key}</code>
        {skipped && ' · not sent'}
      </span>
    </div>
  );
}

/** `Aug 26, 2:41 pm`. Local to whoever is reading, which for this screen is right. */
function when(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
